import { SettingsIcon, DatabaseIcon } from "lucide-react";
import { useState, useEffect, useMemo } from "react";

import { useFunctionDialogContext } from "@/components/FunctionsModal";
import { getBuiltInToolsForProvider } from "@/components/FunctionsModal/built-in-tools";
import { usePlayground } from "@/components/Playground/playground-store";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { BuiltInTool, BuiltInToolProvider, Function_ } from "@/types";
import { displayErrorToast } from "@/utils/toast";
import { isBuiltInTool } from "@/utils/utils";
import { VectorStoreModal } from "@/components/VectorStore/VectorStoreModal";
import { GoogleFileSearchStoreModal } from "@/components/GoogleFileSearchStore";

const BuiltInToolsDialog = () => {
  const [selectedBuiltInTool, setSelectedBuiltInTool] = useState<string>("");
  const [editableConfig, setEditableConfig] = useState<string>("");
  const [isVectorStoreModalOpen, setIsVectorStoreModalOpen] = useState(false);
  const [isGoogleFileSearchStoreModalOpen, setIsGoogleFileSearchStoreModalOpen] = useState(false);
  const {
    functions,
    setFunctions,
    setIsShowingBuiltInTools,
    setIsAddingFunction,
    setEditingFunction,
    editingFunction,
  } = useFunctionDialogContext();
  const playground = usePlayground();

  const currentProvider = playground.provider?.toLowerCase() as BuiltInToolProvider | undefined;
  const { model } = playground;

  const availableBuiltInTools = useMemo(
    () =>
      currentProvider
        ? getBuiltInToolsForProvider(currentProvider, {
            modelName: model?.name,
            baseModel: model?.base_model ?? undefined,
            apiType: model?.api_type,
          })
        : [],
    [currentProvider, model?.name, model?.base_model, model?.api_type],
  );

  const isEditingMode = editingFunction && "id" in editingFunction && "provider" in editingFunction;

  useEffect(() => {
    if (isEditingMode && editingFunction) {
      const builtInTool = editingFunction as BuiltInTool;
      const matchingTool = availableBuiltInTools.find(
        tool =>
          tool.id === builtInTool.id ||
          (tool.type === builtInTool.type && tool.provider === builtInTool.provider),
      );

      if (matchingTool) {
        setSelectedBuiltInTool(matchingTool.id);
        setEditableConfig(JSON.stringify(builtInTool.config, null, 2));
      }
    } else if (!isEditingMode && availableBuiltInTools.length > 0 && !selectedBuiltInTool) {
      const webSearchTool = availableBuiltInTools.find(tool => tool.type === "web_search");
      const defaultTool = webSearchTool || availableBuiltInTools[0];

      if (defaultTool) {
        setSelectedBuiltInTool(defaultTool.id);
        setEditableConfig(JSON.stringify(defaultTool.config, null, 2));
      }
    }
  }, [availableBuiltInTools, selectedBuiltInTool, isEditingMode, editingFunction]);

  const handleClose = () => {
    setSelectedBuiltInTool("");
    setEditableConfig("");
    setIsShowingBuiltInTools(false);
    setIsAddingFunction(false);
    setEditingFunction(null);
  };

  const handleSave = () => {
    if (!selectedBuiltInTool || !setFunctions) {
      return;
    }

    const selectedTool = availableBuiltInTools.find(tool => tool.id === selectedBuiltInTool);
    if (!selectedTool) {
      return;
    }

    let parsedConfig;
    try {
      const sanitized = editableConfig ? editableConfig.replace(/,\s*([\]}])/g, "$1") : "";
      parsedConfig = sanitized ? JSON.parse(sanitized) : selectedTool.config;
    } catch {
      displayErrorToast(
        "Invalid JSON in tool configuration. Please check the syntax and try again.",
      );
      return;
    }

    const builtInToolFunction: BuiltInTool = {
      id: selectedTool.id,
      name: selectedTool.name,
      description: selectedTool.description,
      provider: selectedTool.provider,
      type: selectedTool.type,
      config: parsedConfig,
    };

    if (isEditingMode && editingFunction) {
      const editingBuiltIn = editingFunction as BuiltInTool;
      const functionIndex = functions.findIndex(
        func =>
          isBuiltInTool(func) &&
          (func as BuiltInTool).id === editingBuiltIn.id &&
          JSON.stringify((func as BuiltInTool).config) === JSON.stringify(editingBuiltIn.config),
      );
      if (functionIndex !== -1) {
        const updatedFunctions = [...functions];
        updatedFunctions[functionIndex] = builtInToolFunction;
        setFunctions(updatedFunctions);
      }
    } else {
      if (selectedTool.type !== "openai_mcp") {
        const hasDuplicate = functions.some(
          (func: Function_) =>
            isBuiltInTool(func) && (func as BuiltInTool).type === selectedTool.type,
        );
        if (hasDuplicate) {
          displayErrorToast(
            `A ${selectedTool.name} tool is already added. Please edit the existing tool instead.`,
          );
          return;
        }
      }
      setFunctions([...functions, builtInToolFunction]);
    }

    handleClose();
  };

  const handleVectorStoreSelect = (vectorStoreIds: string[]) => {
    try {
      const config = editableConfig ? JSON.parse(editableConfig) : {};
      const realVectorStoreIds = vectorStoreIds.filter(
        id => !id.startsWith("<") && !id.endsWith(">"),
      );
      config.vector_store_ids = realVectorStoreIds;
      setEditableConfig(JSON.stringify(config, null, 2));
    } catch {
      displayErrorToast("Error processing vector store select");
    }
  };

  const handleGoogleFileSearchStoreSelect = (storeNames: string[]) => {
    try {
      const config = editableConfig ? JSON.parse(editableConfig) : {};
      const realStoreNames = storeNames.filter(
        name => !name.startsWith("<") && !name.endsWith(">"),
      );
      if (!config.file_search) {
        config.file_search = {};
      }
      config.file_search.file_search_store_names = realStoreNames;
      setEditableConfig(JSON.stringify(config, null, 2));
    } catch {
      displayErrorToast("Error processing file search store select");
    }
  };

  const selectedTool = availableBuiltInTools.find(t => t.id === selectedBuiltInTool);
  const isOpenAIFileSearch =
    selectedTool?.type === "file_search" && selectedTool?.provider === "openai";
  const isGoogleFileSearch =
    selectedTool?.type === "file_search" && selectedTool?.provider === "google";

  return (
    <>
      <VectorStoreModal
        open={isVectorStoreModalOpen}
        onClose={() => setIsVectorStoreModalOpen(false)}
        onSelect={handleVectorStoreSelect}
        initialSelectedIds={(() => {
          try {
            const config = editableConfig ? JSON.parse(editableConfig) : {};
            return config.vector_store_ids || [];
          } catch {
            return [];
          }
        })()}
      />
      <GoogleFileSearchStoreModal
        open={isGoogleFileSearchStoreModalOpen}
        onClose={() => setIsGoogleFileSearchStoreModalOpen(false)}
        onSelect={handleGoogleFileSearchStoreSelect}
        provider="google"
        initialSelectedNames={(() => {
          try {
            const config = editableConfig ? JSON.parse(editableConfig) : {};
            return config.file_search?.file_search_store_names || [];
          } catch {
            return [];
          }
        })()}
      />
      <div className="space-y-6">
        <div>
          <Label className="text-lg font-semibold">
            {currentProvider
              ? `${
                  currentProvider.charAt(0).toUpperCase() + currentProvider.slice(1)
                } Built-in Tools`
              : "Built-in Tools"}
          </Label>
        </div>

        {availableBuiltInTools.length > 0 ? (
          <>
            <div>
              <Select
                value={selectedBuiltInTool}
                onValueChange={value => {
                  setSelectedBuiltInTool(value);
                  const tool = availableBuiltInTools.find(t => t.id === value);
                  if (tool) {
                    setEditableConfig(JSON.stringify(tool.config, null, 2));
                  }
                }}
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Select a built-in tool" />
                </SelectTrigger>
                <SelectContent>
                  {availableBuiltInTools.map(tool => (
                    <SelectItem key={tool.id} value={tool.id}>
                      <div className="flex items-center gap-2">
                        <SettingsIcon className="h-4 w-4" />
                        {tool.name}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {selectedBuiltInTool && (
              <div className="rounded-lg bg-gray-50 p-4">
                {(() => {
                  const tool = availableBuiltInTools.find(t => t.id === selectedBuiltInTool);
                  return tool ? (
                    <div>
                      <div className="mb-1 text-sm font-medium text-gray-900">{tool.name}</div>
                      <div className="mb-3 text-sm text-gray-600">{tool.description}</div>
                      {isOpenAIFileSearch && (
                        <div className="mb-3">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setIsVectorStoreModalOpen(true)}
                            className="w-full"
                          >
                            <DatabaseIcon className="mr-2 h-4 w-4" />
                            Manage Vector Stores
                          </Button>
                        </div>
                      )}
                      {isGoogleFileSearch && (
                        <div className="mb-3">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setIsGoogleFileSearchStoreModalOpen(true)}
                            className="w-full"
                          >
                            <DatabaseIcon className="mr-2 h-4 w-4" />
                            Manage File Search Stores
                          </Button>
                        </div>
                      )}
                      <Textarea
                        value={editableConfig}
                        onChange={e => setEditableConfig(e.target.value)}
                        className="min-h-[120px] resize-y bg-white font-mono text-xs text-gray-900"
                        placeholder="Tool configuration JSON..."
                      />
                    </div>
                  ) : null;
                })()}
              </div>
            )}

            <div className="flex gap-2 pt-4">
              <Button onClick={handleSave} disabled={!selectedBuiltInTool} className="flex-1">
                {isEditingMode ? "Update Tool" : "Add Tool"}
              </Button>
              <Button variant="outline" onClick={handleClose}>
                Cancel
              </Button>
            </div>
          </>
        ) : (
          <div className="py-8 text-center">
            <SettingsIcon className="mx-auto mb-4 h-12 w-12 text-gray-400" />
            <div className="text-sm text-gray-500">
              No built-in tools available for {currentProvider || "this provider"}
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default BuiltInToolsDialog;
